import { useCallback, useEffect, useRef, useState } from "react";
import { MicroLabel } from "@/components/ui/MicroLabel";
import type { Dispatch, RefObject, SetStateAction } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { BorrowSource, Definitions } from "@/lib/element-registry";
import type { Beside, Drop } from "./popover-room";
import { besideRow, dropFrom } from "./popover-room";
import { borrowable, repeatable } from "@/lib/element-registry";
import { blank } from "@/lib/figure-status";
import { cn } from "@/lib/utils";
import type { NamedFigure } from "./TitleOffers";
import { figureLabel } from "./TitleOffers";
import { useDismissed } from "@/components/ui/dismiss";
import { ADD_BUTTON } from "./panel-styles";

/** One element as the menu offers it: what it is called, and where it is being taken from. */
interface Offer {
  elementId: string;
  numeral: string | undefined;
  title: string;
  /** The drawing it is taken from: its id, which is unique, and its name, which is not. */
  fromId: string;
  from: string;
}

/** How wide a lending drawing's list is, which is also what decides the side it opens on. */
const FLYOUT_WIDTH = 222;

/** The heights the design draws these two panels at, where the window has room for them. */
const FLYOUT_TALLEST = 300;
const MENU_TALLEST = 328;

/** The lift both surfaces sit on, which is deeper and softer than the house shadow. */
const RAISED =
  "shadow-[0_14px_34px_-10px_rgba(15,0,27,0.28),0_2px_6px_rgba(15,0,27,0.06)]";

const MENU = `absolute left-0 z-30 flex w-[266px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-menu border border-border bg-card p-[5px] ${RAISED}`;

/* Where the heading sits, which is the menu's layout rather than the caption's. */
const MENU_HEADING = "flex items-center gap-1.5 px-2.5 pb-[3px] pt-[7px]";

const ITEM =
  "group/item flex h-[29px] w-full items-center gap-[9px] rounded-[6px] px-2.5 text-left text-detail font-medium text-foreground hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30";

const CHIP =
  "min-w-[26px] flex-none rounded-[5px] bg-muted px-[5px] py-px text-center font-mono text-[11.5px] font-semibold text-primary group-hover/item:bg-white/20 group-hover/item:text-primary-foreground";

const TAG =
  "flex-none rounded-[5px] bg-secondary px-[5px] py-px font-mono text-[10px] font-semibold text-muted-foreground group-hover/item:bg-white/20 group-hover/item:text-primary-foreground";

const COUNT =
  "ml-auto rounded-[5px] bg-secondary px-[5px] py-px font-mono text-[11px] font-semibold text-muted-foreground";

/** What an element is called in the menu, which is never a blank line. */
function titleOf(definitions: Definitions, elementId: string): string {
  const title = definitions.registry.find(
    (r) => r.element_id === elementId,
  )?.title;
  return title === undefined || blank(title) ? "(untitled)" : title;
}

function offerOf(
  definitions: Definitions,
  assigned: Map<string, string>,
  elementId: string,
  fromId: string,
  from: string,
): Offer {
  return {
    elementId,
    numeral: assigned.get(elementId),
    title: titleOf(definitions, elementId),
    fromId,
    from,
  };
}

/** One element, offered to be placed. */
function OfferRow({
  offer,
  onChoose,
}: {
  offer: Offer;
  onChoose: (elementId: string) => void;
}) {
  return (
    <button
      type="button"
      className={ITEM}
      onClick={() => {
        onChoose(offer.elementId);
      }}
    >
      <span className={CHIP}>{offer.numeral}</span>
      <span className="min-w-0 flex-1 truncate">{offer.title}</span>
      {offer.from !== "" && <span className={TAG}>{offer.from}</span>}
    </button>
  );
}

/** Where a row's list goes right now, measured from the row itself. */
function placeBeside(
  row: HTMLElement | null,
  rows: number,
): Beside | undefined {
  const box = row?.getBoundingClientRect();
  if (box === undefined) return undefined;
  return besideRow(
    box,
    { width: window.innerWidth, height: window.innerHeight },
    rows,
    FLYOUT_WIDTH,
    FLYOUT_TALLEST,
  );
}

/** What one lending drawing's open list is anchored to, as that moves. */
type Held = { at: Beside; pinned: boolean } | undefined;

/**
 * Keeps an open list up with the row it hangs off.
 *
 * The page scrolls, the menu's own list scrolls, and the window resizes. A
 * panel positioned against the viewport would stay where it was put and drift
 * away from the name it belongs to.
 */
function useFollowsRow(
  showing: boolean,
  rowRef: RefObject<HTMLButtonElement | null>,
  rows: number,
  setHeld: Dispatch<SetStateAction<Held>>,
) {
  useEffect(() => {
    if (!showing) return undefined;
    function follow() {
      const place = placeBeside(rowRef.current, rows);
      if (place === undefined) return;
      setHeld((was) => (was === undefined ? was : { ...was, at: place }));
    }
    window.addEventListener("resize", follow);
    window.addEventListener("scroll", follow, true);
    return () => {
      window.removeEventListener("resize", follow);
      window.removeEventListener("scroll", follow, true);
    };
  }, [showing, rows, rowRef, setHeld]);
}

/** One lending drawing's elements, floated beside its name. */
function SourceFlyout({
  id,
  at,
  source,
  definitions,
  assigned,
  onChoose,
}: {
  id: string;
  at: Beside;
  source: BorrowSource;
  definitions: Definitions;
  assigned: Map<string, string>;
  onChoose: (elementId: string) => void;
}) {
  return (
    <div
      id={id}
      style={{
        left: at.left,
        top: at.top,
        width: FLYOUT_WIDTH,
        maxHeight: at.maxHeight,
      }}
      className={cn(
        "fixed z-40 overflow-y-auto overscroll-contain rounded-menu border border-border bg-card p-[5px]",
        RAISED,
      )}
    >
      {source.elementIds.map((elementId) => (
        <OfferRow
          key={elementId}
          offer={offerOf(definitions, assigned, elementId, source.fileId, "")}
          onChoose={onChoose}
        />
      ))}
    </div>
  );
}

/**
 * One lending drawing, with its elements beside its name.
 *
 * Beside rather than below: a set of eight drawings would otherwise open as one list long
 * enough to hide the drawing each element came from, which is the only thing that tells the
 * attorney what a borrow will share.
 *
 * Three ways in, because hover is only one kind of hand. The pointer opens it by arriving and
 * closes it by leaving; a press pins it open until it is pressed again, which is the only
 * route a touch screen has; and focus reaching the name opens it for the keyboard. A pinned
 * list ignores the pointer leaving, so a tap does not undo itself.
 */
function SourceRow({
  source,
  figures,
  definitions,
  assigned,
  onChoose,
}: {
  source: BorrowSource;
  figures: NamedFigure[];
  definitions: Definitions;
  assigned: Map<string, string>;
  onChoose: (elementId: string) => void;
}) {
  const [held, setHeld] = useState<Held>(undefined);
  const rowRef = useRef<HTMLButtonElement>(null);
  const listId = `reuse-from-${source.fileId}`;

  const rows = source.elementIds.length;
  const show = (pinned: boolean) => {
    const place = placeBeside(rowRef.current, rows);
    if (place !== undefined) setHeld({ at: place, pinned });
  };
  const hide = () => {
    setHeld(undefined);
  };

  useFollowsRow(held !== undefined, rowRef, rows, setHeld);

  return (
    <div
      className="group/sub relative"
      onMouseEnter={() => {
        if (held === undefined) show(false);
      }}
      onMouseLeave={() => {
        if (held?.pinned !== true) hide();
      }}
      // Closed when the keyboard leaves the drawing and its list together, rather than on the
      // first blur: moving from the name into its own elements is a move within one thing.
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) hide();
      }}
    >
      <button
        ref={rowRef}
        type="button"
        // Named in words, because the count read as part of the name gives "FIG. 2 3", which
        // says nothing about what the second number counts.
        aria-label={`${figureLabel(figures, source.fileId)}, ${String(source.elementIds.length)} numerals`}
        // Lit from the group rather than from itself, so it stays lit while the pointer is
        // inside its own list: the name and the elements under it are one thing.
        className={cn(
          ITEM,
          "font-semibold group-hover/sub:bg-primary group-hover/sub:text-primary-foreground",
        )}
        aria-expanded={held !== undefined}
        aria-controls={held === undefined ? undefined : listId}
        onFocus={() => {
          if (held === undefined) show(false);
        }}
        onClick={() => {
          if (held?.pinned === true) hide();
          else show(true);
        }}
      >
        {figureLabel(figures, source.fileId)}
        <span className={COUNT} aria-hidden>
          {source.elementIds.length}
        </span>
        <ChevronRight
          className="size-[13px] flex-none opacity-80"
          aria-hidden
        />
      </button>
      {held !== undefined && (
        <SourceFlyout
          id={listId}
          at={held.at}
          source={source}
          definitions={definitions}
          assigned={assigned}
          onChoose={onChoose}
        />
      )}
    </div>
  );
}

/**
 * Every offer flattened, so a filter reads one list rather than opening each drawing in turn.
 *
 * A hit carries the drawing it came from, because the same element can be borrowed from more
 * than one place and a bare number would not say which borrow is being started.
 */
function hits(
  definitions: Definitions,
  assigned: Map<string, string>,
  figures: NamedFigure[],
  here: string[],
  sources: BorrowSource[],
  typed: string,
): Offer[] {
  const wanted = typed.trim().toLowerCase();
  const matches = (offer: Offer) =>
    (offer.numeral !== undefined && offer.numeral.includes(wanted)) ||
    offer.title.toLowerCase().includes(wanted) ||
    offer.from.toLowerCase().includes(wanted);
  const own = here.map((elementId) =>
    offerOf(definitions, assigned, elementId, "", ""),
  );
  const lent = sources.flatMap((source) =>
    source.elementIds.map((elementId) =>
      offerOf(
        definitions,
        assigned,
        elementId,
        source.fileId,
        figureLabel(figures, source.fileId),
      ),
    ),
  );
  return [...own, ...lent].filter(matches);
}

/**
 * The menu's list: the matches while a filter is typed, or the offers grouped
 * by where they come from while it is not.
 */
function ReuseList({
  narrowing,
  filtered,
  here,
  sources,
  figures,
  definitions,
  assigned,
  onChoose,
}: {
  narrowing: boolean;
  filtered: Offer[];
  here: string[];
  sources: BorrowSource[];
  figures: NamedFigure[];
  definitions: Definitions;
  assigned: Map<string, string>;
  onChoose: (elementId: string) => void;
}) {
  return narrowing ? (
    <>
      <div className="flex-none px-2.5 pb-[5px] pt-0.5 text-[10.5px] text-muted-foreground">
        {`${String(filtered.length)} match${filtered.length === 1 ? "" : "es"}`}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {filtered.length === 0 ? (
          <div className="px-2.5 py-2 text-[11.5px] text-muted-foreground">
            Nothing matches that.
          </div>
        ) : (
          filtered.map((offer) => (
            <OfferRow
              key={`${offer.fromId}-${offer.elementId}`}
              offer={offer}
              onChoose={onChoose}
            />
          ))
        )}
      </div>
    </>
  ) : (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      {sources.length > 0 && (
        <>
          <MicroLabel as="div" size="sm" className={MENU_HEADING}>
            From another figure
          </MicroLabel>
          {sources.map((source) => (
            <SourceRow
              key={source.fileId}
              source={source}
              figures={figures}
              definitions={definitions}
              assigned={assigned}
              onChoose={onChoose}
            />
          ))}
        </>
      )}
      {here.length > 0 && (
        <>
          {sources.length > 0 && (
            <div className="mx-[9px] my-[5px] h-px bg-border" />
          )}
          <MicroLabel as="div" size="sm" className={MENU_HEADING}>
            On this figure
          </MicroLabel>
          {here.map((elementId) => (
            <OfferRow
              key={elementId}
              offer={offerOf(definitions, assigned, elementId, "", "")}
              onChoose={onChoose}
            />
          ))}
        </>
      )}
    </div>
  );
}

/**
 * Where the menu opens and how tall it may be, measured off its trigger.
 *
 * The button is on screen whether or not the menu is, so its rect can be read
 * the moment it is pressed; `measure` is handed back for the press to call.
 */
function useMenuRoom(
  open: boolean,
  triggerRef: RefObject<HTMLButtonElement | null>,
) {
  const [room, setRoom] = useState<Drop>({
    drop: "below",
    maxHeight: MENU_TALLEST,
  });

  // A menu given a fixed height reaches past the bottom of a short window, and
  // the panel it hangs off is sticky, so the page cannot be scrolled to bring
  // the rest of it back. The button is on screen whether or not the menu is,
  // so its rect can be read the moment it is pressed.
  const measure = useCallback(() => {
    const trigger = triggerRef.current?.getBoundingClientRect();
    if (trigger === undefined) return;
    setRoom(
      dropFrom(
        trigger,
        { width: window.innerWidth, height: window.innerHeight },
        MENU_TALLEST,
      ),
    );
  }, [triggerRef]);

  // Measured again on anything that moves the window under an open menu.
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, measure]);

  return { room, measure };
}

/** Typing narrows the list; it takes the caret when the menu opens. */
function ReuseFilter({
  typed,
  onTyped,
}: {
  typed: string;
  onTyped: (next: string) => void;
}) {
  return (
    <div className="flex-none px-[3px] pb-[5px] pt-[3px]">
      <input
        // The caret goes here on opening, so typing is the fast path to a numeral.
        autoFocus
        value={typed}
        onChange={(event) => {
          onTyped(event.target.value);
        }}
        placeholder="Filter by figure, number or title"
        autoComplete="off"
        className="w-full rounded-[7px] border border-input bg-card px-[9px] py-1.5 text-[12px] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
      />
    </div>
  );
}

/** The button the menu hangs off, disabled when there is nothing to re-use. */
function ReuseTrigger({
  ref,
  open,
  nothing,
  onToggle,
}: {
  ref: RefObject<HTMLButtonElement | null>;
  open: boolean;
  nothing: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={nothing}
      aria-expanded={open}
      title={
        nothing
          ? "No elements to re-use yet"
          : "Place an element that already exists"
      }
      onClick={onToggle}
      className={cn(
        ADD_BUTTON,
        open
          ? "border-transparent bg-primary text-primary-foreground"
          : "border-border bg-card",
        nothing && "opacity-45",
      )}
    >
      Re-use a numeral
      <ChevronDown className="size-3" aria-hidden />
    </button>
  );
}

/**
 * The elements the set already holds, offered to be marked on this drawing.
 *
 * Two ways in, under one button, because they are one question: which existing part is this.
 * An element from this drawing places a second mark of a part already marked here, and one
 * from another drawing shares a part across the set under the number it already carries.
 * Minting is the other question and stays on its own button.
 *
 * The other figures come first and this one last, each headed, so the rows do not move
 * between two openings as this figure gains numerals.
 *
 * The menu keeps no choice of its own. Picking is an instruction to start a placement rather
 * than a setting, so it closes on the pick and reopens offering everything, and what was
 * typed into the filter goes with it.
 */
export function ReuseMenu({
  definitions,
  assigned,
  fileId,
  figures,
  onChoose,
}: {
  definitions: Definitions;
  assigned: Map<string, string>;
  fileId: string;
  figures: NamedFigure[];
  onChoose: (elementId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { room, measure } = useMenuRoom(open, triggerRef);

  const shut = () => {
    setOpen(false);
    setTyped("");
  };

  /**
   * Picking one, which closes the menu and puts the keyboard back on the button that opened
   * it. Not folded into shut(), which also runs when the attorney presses somewhere else:
   * taking focus back then would pull it off whatever they had just pressed.
   */
  const pick = (elementId: string) => {
    onChoose(elementId);
    shut();
    triggerRef.current?.focus();
  };
  useDismissed(open, panelRef, triggerRef, shut);

  const here = repeatable(definitions, fileId);
  const sources = borrowable(definitions, fileId);
  const nothing = here.length === 0 && sources.length === 0;
  const filtered = hits(definitions, assigned, figures, here, sources, typed);
  const narrowing = typed.trim() !== "";

  return (
    <div className="relative">
      <ReuseTrigger
        ref={triggerRef}
        open={open}
        nothing={nothing}
        onToggle={() => {
          if (open) shut();
          else {
            measure();
            setOpen(true);
          }
        }}
      />

      {open && (
        <div
          ref={panelRef}
          style={{
            maxHeight: room.maxHeight,
            [room.drop === "below" ? "top" : "bottom"]: "calc(100% + 5px)",
          }}
          className={MENU}
        >
          <ReuseFilter typed={typed} onTyped={setTyped} />

          <ReuseList
            narrowing={narrowing}
            filtered={filtered}
            here={here}
            sources={sources}
            figures={figures}
            definitions={definitions}
            assigned={assigned}
            onChoose={pick}
          />
        </div>
      )}
    </div>
  );
}
